// 家用面板的裝置頁（P1a 最小版）：狀態區（V-02 的一部分：在線 / 最後聯絡 / 照片張數 / 需要重新加入）＋ V-16 移除。
// P2 / P3：V-03 相簿（主人 + 可上傳照片的分享者）、V-04 顯示設定（只限主人）。
// P5（只限主人）：V-05 插播設定與統計。
// L1（只限主人）：小工具版面的歷史與還原（07-dashboard §7）。
// L2（只限主人）：時鐘小工具的天氣地點（07-dashboard §3.4）。
// P4（只限主人）：V-02 待辦與 PIN 狀態、V-06 門鈴與警報、V-10 家人、V-15 操作紀錄。
// 2026-10-21：重新命名（只有主人；同一般裝置 PATCH /v3/devices/{id} display_name）、［面板 PIN］入口（就地展開帳號頁那張卡）。
// 2026-10-22：打電話時顯示的名字（主人）/ 我怎麼稱呼這台（每個人）；［打給這台面板］、家人自動接聽（主人）。
// 2026-10-22：VC1a 行事曆入口（行事曆家人）、長輩名字 / 叫法 / 能管行事曆（主人；09 §5.3、§8）。
// 2026-10-23：VC2 語音設定與聽不懂統計（主人；V-24，09 §2.7、§8）。
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { formatRelativeUs } from '../../lib/format';
import { deviceName, deviceStateLabel, tileState, type DeviceSummary } from './deviceModel';
import { removePanel } from './panels';
import { PanelCapsCard, PanelNetworksCard } from './PanelP1bCards';
import { PanelPendingBadge } from './PanelCalNotices';
import { PanelAlbumCard, PanelDisplayCard } from './PanelP2Cards';
import { PanelInterruptCard } from './PanelP5Cards';
import { PanelLayoutCard } from './PanelL1Cards';
import { PanelWeatherCard } from './PanelL2Cards';
import { MyPanelPinCard, PanelActivityCard, PanelBellCard, PanelHomeCard, PanelMembersCard, PanelTodoCard } from './PanelP4Cards';
import { PanelCallNameCard } from './PanelNameCards';
import { PanelCallCard } from './PanelCallCards';
import { PanelCalendarEntryCard, PanelCalendarPeopleCard } from './PanelCalendarCards';
import { PanelVoiceCard } from './PanelVoiceCard';
import { useDeviceList } from './useDeviceList';
import { canUploadPhotos } from './panels';

export function PanelDetail({ device, onRemoved }: { device: DeviceSummary; onRemoved: () => void }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [homeKey, setHomeKey] = useState(0);   // 主畫面模式切換後，待辦與主畫面卡一起重讀
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owner = device.role === 'owner';
  const st = tileState(device);
  const p = device.panel ?? {};
  const { devices, reload } = useDeviceList();
  const [name, setName] = useState(() => deviceName(device));
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);

  // 重新命名：同 DeviceSettings.saveRename（1–32 字、樂觀更新、失敗還原）
  const saveRename = async () => {
    const next = nameDraft.trim();
    if (next.length < 1 || next.length > 32) {
      setRenameError(t('deviceSettings.nameLengthError'));
      return;
    }
    setRenameBusy(true);
    setRenameError(null);
    const prev = name;
    setName(next);
    try {
      await apiFetch(`/v3/devices/${encodeURIComponent(device.device_id)}`, { method: 'PATCH', body: { display_name: next } });
      setRenaming(false);
      void reload();
    } catch (err) {
      setName(prev);
      setRenameError(err instanceof ApiError ? t('deviceSettings.renameFailedReason', { msg: err.message }) : t('deviceSettings.renameFailed'));
    } finally {
      setRenameBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await removePanel(device.device_id);
      onRemoved();
    } catch {
      setError(t('panels.errGeneric'));
      setBusy(false);
    }
  };

  return (
    <section data-testid="panel-detail">
      <header className="dv-detail-head">
        <div>
          {renaming ? (
            <span className="dv-pairing-actions" data-testid="panel-rename-form">
              <input value={nameDraft} maxLength={32} onChange={(e) => setNameDraft(e.target.value)} aria-label={t('deviceSettings.deviceName')}
                onKeyDown={(e) => { if (e.key === 'Enter') void saveRename(); }} />
              <button className="btn-primary" disabled={renameBusy} onClick={() => void saveRename()}>
                {renameBusy ? t('common.saving') : t('common.save')}
              </button>
              <button className="btn-ghost" disabled={renameBusy} onClick={() => { setRenaming(false); setRenameError(null); }}>
                {t('common.cancel')}
              </button>
            </span>
          ) : (
            <h1>
              {name}
              {owner && <PanelPendingBadge panelId={device.device_id} />}
              {owner && (
                <button className="btn-ghost" data-testid="panel-rename" style={{ marginLeft: 'var(--spacing-4)', padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
                  onClick={() => { setNameDraft(name); setRenaming(true); }}>
                  {t('deviceSettings.rename')}
                </button>
              )}
            </h1>
          )}
          {renameError && <p className="error-text">{renameError}</p>}
          <div className="dv-detail-sub">
            <span className={`dv-dot dv-dot-${st}`} />
            <span className="text-secondary">
              {deviceStateLabel(device)}
              {p.last_contact_at ? t('panels.lastContact', { time: formatRelativeUs(p.last_contact_at) }) : ''}
            </span>
            <span className="dv-badge">{t('devices.tileBadge_panel')}</span>
          </div>
        </div>
        <Link className="btn-ghost" to="/devices">
          {t('devices.backToList')}
        </Link>
      </header>

      {/* 照片張數放相簿卡（B13：上方再列一次跟相簿重疊） */}
      {p.needs_rejoin && (
        <div className="card">
          <dl className="dv-info">
            <dt>{t('panels.status')}</dt>
            <dd className="error-text">{t('panels.needsRejoin')}</dd>
          </dl>
        </div>
      )}

      {/* 面板 PIN：在面板上編輯版面 / 開門 / 離開面板要輸入的 PIN（同帳號頁那張，就地展開） */}
      {showPin ? (
        <MyPanelPinCard />
      ) : (
        <div className="card">
          <button className="btn-ghost" data-testid="panel-pin-entry" onClick={() => setShowPin(true)}>
            {t('panels.pinEntry')} ›
          </button>
          <p className="dv-detail-sub">{t('panels.pinEntryHint')}</p>
        </div>
      )}
      <PanelCallNameCard panelId={device.device_id} panelName={name} isOwner={owner} />
      <PanelCallCard panelId={device.device_id} panelName={name} isOwner={owner} />
      <PanelCalendarEntryCard panelId={device.device_id} isOwner={owner} />
      {owner && <PanelCalendarPeopleCard panelId={device.device_id} />}
      {owner && <PanelVoiceCard panelId={device.device_id} />}

      {owner && <PanelTodoCard panelId={device.device_id} reloadKey={homeKey} onHomeChanged={() => setHomeKey((k) => k + 1)} />}
      {owner && <PanelHomeCard panelId={device.device_id} reloadKey={homeKey} onChanged={() => setHomeKey((k) => k + 1)} />}
      <PanelAlbumCard panelId={device.device_id} isOwner={owner} canUpload={canUploadPhotos(device)} />
      {owner && <PanelDisplayCard panelId={device.device_id} />}
      {owner && <PanelLayoutCard panelId={device.device_id} />}
      {owner && <PanelWeatherCard panelId={device.device_id} />}
      {owner && <PanelNetworksCard panelId={device.device_id} onRemoved={onRemoved} />}
      {owner && <PanelInterruptCard panelId={device.device_id} devices={devices ?? []} onDevicesChanged={() => void reload()} />}
      {owner && <PanelBellCard panelId={device.device_id} devices={devices ?? []} />}
      {owner && <PanelMembersCard panelId={device.device_id} devices={devices ?? []} />}
      {owner && <PanelCapsCard panelId={device.device_id} />}
      {owner && <PanelActivityCard panelId={device.device_id} />}

      {owner && (
        <div className="card" data-testid="panel-remove">
          {!confirming ? (
            <button className="btn-danger" onClick={() => setConfirming(true)}>
              {t('panels.remove')}
            </button>
          ) : (
            <>
              <p>{t('panels.removeConfirm')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-danger" onClick={() => void remove()} disabled={busy}>
                  {t('panels.removeYes')}
                </button>
                <button className="btn-ghost" onClick={() => setConfirming(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
              </div>
            </>
          )}
          {error && <p className="error-text">{error}</p>}
        </div>
      )}
    </section>
  );
}
